import { configQuery } from '@gig/magic-common';
import { __awaiter, __generator } from 'tslib';
import { mapToKycStatus, magic, mapToAmlStatus } from '@gig/magic-sdk';
import { Currency } from '@gig/magic-sdk/dist/adapter/users/signup';
import { DocumentType, AmlStatus } from '@gig/magic-sdk/dist/adapter/users/getAmlStatus';
import { shouldBlockUser } from '../../utils/kyc-corex.js';

function fetchProfileData() {
    return __awaiter(this, void 0, void 0, function () {
        var _a, profile, magicProfile, documents, _b, _c, _d;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _c = (_b = Promise).all;
                    _d = [magic.methods.users.getUserProfile(),
                        magic.methods.users.getMagicProfileCoreX()];
                    return [4 /*yield*/, magic.invokeCached(60, magic.methods.users, 'getUserDocuments')()];
                case 1: return [4 /*yield*/, _c.apply(_b, [_d.concat([
                            (_e.sent()) || []
                        ])])];
                case 2:
                    _a = _e.sent(), profile = _a[0], magicProfile = _a[1], documents = _a[2];
                    return [2 /*return*/, {
                            profile: profile,
                            magicProfile: magicProfile,
                            documents: documents,
                        }];
            }
        });
    });
}
function mapToUser(userId, email, sessionId, profileData, magicProfile, documents) {
    var _a, _b;
    return __awaiter(this, void 0, void 0, function () {
        var _c, compliance, user, additionalData, country, apiCurrency, configCurrencyRaw, configCurrency, resolvedCurrency;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    _c = profileData, compliance = _c.compliance, user = _c.user, additionalData = _c.additionalData;
                    country = user.address.addressCountry;
                    apiCurrency = (_a = additionalData['registration.currencyCode']) === null || _a === void 0 ? void 0 : _a.toUpperCase();
                    return [4 /*yield*/, configQuery('defaultCurrency')];
                case 1:
                    configCurrencyRaw = _d.sent();
                    configCurrency = (_b = configCurrencyRaw === null || configCurrencyRaw === void 0 ? void 0 : configCurrencyRaw.trim()) === null || _b === void 0 ? void 0 : _b.toUpperCase();
                    resolvedCurrency = apiCurrency && apiCurrency.trim() !== ''
                        ? apiCurrency
                        : configCurrency && configCurrency.trim() !== ''
                            ? configCurrency
                            : Currency.EUR;
                    return [2 /*return*/, {
                            id: userId,
                            userId: userId,
                            email: email,
                            userName: user.username,
                            firstName: user.givenName,
                            lastName: user.familyName,
                            sessionId: sessionId,
                            currency: resolvedCurrency,
                            country: country,
                            countryCode: country,
                            birthDate: new Date(user.birthDate).getTime(),
                            createdDate: profileData.createdOn,
                            authMethod: 1,
                            preferences: {
                                showSportsbook: true,
                                showCasinoGames: true,
                            },
                            location: country,
                            lastVisit: magicProfile.lastVisit,
                            loginTries: 0,
                            segment: magicProfile.segment,
                            lang: magicProfile.lang,
                            languageCode: magicProfile.lang,
                            igcToken: '',
                            agreedCompliance: magicProfile.agreedCompliance,
                            notOnSiteMarketing: !!magicProfile.notOnSiteMarketing,
                            affiliate: magicProfile.affiliate,
                            reference: undefined,
                            roles: magicProfile.roles,
                            kycStatus: {
                                address: mapToKycStatus(compliance.addressVerificationStatus),
                                payment: mapToKycStatus(compliance.sourceOfWealthVerificationStatus),
                                photoID: mapToKycStatus(compliance.idVerificationStatus),
                                uploadCounter: {
                                    photoID: 0,
                                    payment: 0,
                                    address: 0,
                                },
                                uploadLimit: {
                                    photoID: null,
                                    payment: null,
                                    address: null,
                                },
                                status: shouldBlockUser(compliance, documents),
                                bankIdBlocked: false,
                                userName: userId,
                                id: userId,
                            },
                            // unsupported fields
                            subdivisionID: '',
                            dataZoo: undefined,
                            isCompleted: false,
                            personId: '',
                            // used only for tracking
                            depositCount: magicProfile.depositCount || 0,
                            // unused fields
                            govermentID: '',
                            acceptedTermsOn: '',
                            accountVerifiedOn: '',
                            emProfileComplete: false,
                            emSessionId: '',
                            excludeFeed: false,
                            lifeFPP: 0,
                            nameSubdivision: '',
                            codeSubdivision: '',
                            pinboard: [],
                            languageLCID: 0,
                            refererId: '',
                            sessionLimit: 0,
                            sessionLimitCreated: 0,
                            leaderboardName: '',
                            source: '',
                            status: '',
                        }];
            }
        });
    });
}
function mapToAmlStatusResponse(profile) {
    var _a, _b, _c, _d;
    return {
        uploadCounter: (_a = {},
            _a[DocumentType.ADDRESS] = 0,
            _a[DocumentType.PAYMENT] = 0,
            _a[DocumentType.PHOTO_ID] = 0,
            _a[DocumentType.SOURCE_OF_FUNDS] = 0,
            _a),
        uploadLimit: (_b = {},
            _b[DocumentType.ADDRESS] = 1,
            _b[DocumentType.PAYMENT] = 1,
            _b[DocumentType.PHOTO_ID] = 1,
            _b[DocumentType.SOURCE_OF_FUNDS] = 1,
            _b),
        bankIdBlocked: false,
        history: (_c = {},
            _c[DocumentType.ADDRESS] = [],
            _c[DocumentType.PAYMENT] = [],
            _c[DocumentType.PHOTO_ID] = [],
            _c[DocumentType.SOURCE_OF_FUNDS] = [],
            _c[DocumentType.SOURCE_OF_WEALTH] = [],
            _c[DocumentType.SOURCE_OF_WEALTH_DOCUMENTS] = [],
            _c),
        status: (_d = {},
            _d[DocumentType.ADDRESS] = mapToAmlStatus(profile.addressVerificationStatus),
            _d[DocumentType.PAYMENT] = mapToAmlStatus(profile.sourceOfWealthVerificationStatus),
            _d[DocumentType.PHOTO_ID] = mapToAmlStatus(profile.idVerificationStatus),
            _d[DocumentType.SOURCE_OF_FUNDS] = mapToAmlStatus(profile.sourceOfFundsVerificationStatus),
            _d[DocumentType.SOURCE_OF_WEALTH] = AmlStatus.NOT_SET,
            _d[DocumentType.SOURCE_OF_WEALTH_DOCUMENTS] = AmlStatus.NOT_SET,
            _d),
    };
}

export { fetchProfileData, mapToAmlStatusResponse, mapToUser };
